import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

/** A component used to present form validation errors in a summary style described by DKFDS
 * (https://designsystem.dk/komponenter/fejlopsummering/)
 */
@Component({
  selector: 'bom-validation-summary',
  templateUrl: './validation-summary.component.html',
  styleUrls: ['./validation-summary.component.scss'],
})
export class ValidationSummaryComponent implements OnInit, OnDestroy {
  @Input() myForm: FormGroup;
  statusChangeSubscription: Subscription;
  errors: ErrorItem[] = [];

  // angular router links with #-fragments only work on first click,
  // add a queryparam counter to handle multiple click on same errory summary
  counter = 0;

  constructor() {}

  ngOnInit() {
    this.statusChangeSubscription = this.myForm.statusChanges.subscribe(status => {
      this.resetErrorMessages();
      this.errors = this.generateErrorMessages(this.myForm);
    });
  }

  ngOnDestroy(): void {
    this.statusChangeSubscription?.unsubscribe();
  }

  resetErrorMessages() {
    this.errors.length = 0;
  }

  incrementCounter() {
    this.counter++;
  }

  generateErrorMessages(formGroup: FormGroup): ErrorItem[] {
    const formErrors: ErrorItem[] = [];

    // Get root level control names
    const controlNames = Object.keys(formGroup.controls);

    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName);

      if (control.invalid) {
        const controlErrors = this.getErrorsFromControl(controlName, control);
        formErrors.push(...controlErrors);
      }
    });

    return formErrors;
  }

  getErrorsFromControl(controlName: string, control: AbstractControl): ErrorItem[] {
    const result: ErrorItem[] = [];

    if (control.invalid) {
      const errors = control.errors;

      if (errors) {
        let errorMessage = '';

        // Try to get custom error message first
        if (errors.message) {
          errorMessage = errors.message;
        }

        // No custom error message, try to build a fallback error message
        if (!errors.message) {
          // Validators.required
          if (errors.required) {
            errorMessage = `${controlName} skal udfyldes`;
          }

          // Validators.minLength
          if (errors.minlength) {
            errorMessage = `${controlName} minimum længde er ${errors.minlength.requiredLength}`;
          }

          // Validators.pattern
          if (errors.pattern) {
            errorMessage = `${controlName} er ikke gyldig`;
          }

          // Catch unknown validation keys (like 'pattern/minlength/custom/...')
          if (errorMessage === '') {
            const keys = Object.keys(errors).join(', ');
            errorMessage = `${controlName} ${keys}`;
          }
        }

        if (errorMessage) {
          const errorItem = { controlName: controlName, errorMessage: errorMessage };
          result.push(errorItem);
        }
      }

      // inspect child controls
      if (control.invalid) {
        const formGroup = control as FormGroup;
        if (formGroup != null && formGroup.controls) {
          const childNames = Object.keys(formGroup.controls);

          childNames.forEach(childName => {
            const childControl = formGroup.get(childName);

            if (childControl.invalid) {
              const childControlErrors = this.getErrorsFromControl(childName, childControl);
              result.push(...childControlErrors);
            }
          });
        }
      }

      return result;
    }

    return result;
  }
}

export interface ErrorItem {
  controlName: string;
  errorMessage: string;
}
